/* eslint-disable prefer-rest-params,no-invalid-this */

/**
 * Sort an array of objects by the given attribute
 *
 * @param {object} a - Attribute of object a (e.g. objA.name)
 * @param {object} b - Attribute of object b (e.g. objB.name)
 * @return {number}
 */
export function sortObjects(a, b) {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

/**
 * Transform a color hex code into RGBA
 *
 * If hex transformation fails, return RGBA white with 100% transparency
 * and log to console
 *
 * @param {string} hex
 * @param {int} opacity
 * @return {string}
 *
 */
export function hexToRgbA(hex, opacity) {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = '0x' + c.join('');
    return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')';
  } else {
    return 'rgba(255,255,255,0.0)';
  }
}

/**
 * Set the height of the element corresponding to the client height +/- an optional offset
 *
 * @return {int}
 */
export function setHeight() {
  let offset = arguments.length > 0 && arguments[0] !== 'undefined' ? arguments[0] : null;

  if (offset !== null) {
    return (document.documentElement.clientHeight + offset);
  }
}

/**
 * Replaces the jQuery function "each"
 * @param {string} selector
 * @param {function} fn
 */
export function forEachElement(selector, fn) {
  let elements = document.querySelectorAll(selector);

  for (let i = 0; i < elements.length; i++) {
    fn(elements[i], i);
  }
}

/**
 * @param {array} coordinates
 * @return {number[]}
 */
export function calculateExtent(coordinates) {
  let largestRectangle = [[0, 0], [0, 0], [0, 0], [0, 0]];
  let currentArea = 0;
  let currentHeight = 0;
  let currentWidth = 0;

  for (let i = 0; i < coordinates.length; i++) {
    let point1 = getPoint(i, coordinates);
    let point2 = getPoint(i + 2, coordinates);
    let width = getWidth(point1, point2);
    let height = getHeight(point1, point2);
    let area = width * height;

    if (currentArea > area) {
      continue;
    }

    if (isRectangleInPolygon(point1, point2, coordinates)) {
      let rect = createRectangle(point1, point2);
      if (Math.abs(currentArea - area) < 0.001) {
        if (currentHeight > height) {
          continue;
        }
        if (currentWidth === width && currentHeight === height) {
          continue;
        }
      }

      largestRectangle = rect;
      currentArea = area;
      currentHeight = height;
      currentWidth = width;
    }
  }
  largestRectangle.push(largestRectangle[0]);
  return largestRectangle;
}

/**
 * @param {int} index
 * @param {array} coordinates
 * @return {*}
 */
function getPoint(index, coordinates) {
  let newCoords = coordinates.slice(0, coordinates.length - 1);
  return newCoords[index % newCoords.length];
}

/**
 * @param {array} point1
 * @param {array} point2
 * @return {number}
 */
function getWidth(point1, point2) {
  return Math.abs(point1[0] - point2[0]);
}

/**
 * @param {array} point1
 * @param {array} point2
 * @return {number}
 */
function getHeight(point1, point2) {
  return Math.abs(point1[1] - point2[1]);
}

/**
 * @param {number} point1
 * @param {number} point2
 * @return {*[]}
 */
function createRectangle(point1, point2) {
  return [
    point1,
    [point2[0], point1[1]],
    point2,
    [point1[0], point2[1]],
  ];
}

/**
 * @param {number} point1
 * @param {number} point2
 * @param {array} points
 * @return {boolean}
 */
function isRectangleInPolygon(point1, point2, points) {
  let pointA = [point1[0], point2[1]];
  if (!test(pointA, points)) {
    return false;
  }

  let pointB = [point2[0], point1[1]];
  return test(pointB, points);
}

/**
 * @param {array} point
 * @param {array} polygon
 * @return {boolean}
 */
export function test(point, polygon) {
  let result = -1;

  for (let i = 0; i < polygon.length; i++) {
    result *= doTest(point, getPoint(i, polygon), getPoint(i + 1, polygon));
    if (result === 0) return true;
  }
  return result === 1;
}

/**
 * @param {array} point
 * @param {array} lineStart
 * @param {array} lineEnd
 * @return {number}
 */
function doTest(point, lineStart, lineEnd) {
  if (areEqual(point[1], lineStart[1]) && areEqual(point[1], lineEnd[1])) {
    if ((lineStart[0] <= point[0] && point[0] <= lineEnd[0]) || (lineEnd[0] <= point[0] && point[0] <= lineStart[0])) {
      return 0;
    }
    return 1;
  }

  if (areEqual(point[1], lineStart[1]) && areEqual(point[0], lineStart[0])) {
    return 0;
  }

  if (lineStart[1] > lineEnd[1]) {
    let temp = lineStart;
    lineStart = lineEnd;
    lineEnd = temp;
  }

  if (point[1] <= lineStart[1] || point[1] > lineEnd[1]) {
    return 1;
  }

  let delta = ((lineStart[0] - point[0]) * (lineEnd[1] - point[1])) - ((lineStart[1] - point[1]) * (lineEnd[0] - point[0]));

  if (delta > 0) {
    return -1;
  }
  return delta < 0 ? 1 : 0;
}

/**
 * @param {number} a
 * @param {number} b
 * @return {boolean}
 */
function areEqual(a, b) {
  return a === b;
}

export function createHTMLElement(elementType, className = null, elementId = null, innerHTML = null) {
  const element = document.createElement(elementType);
  if (className != null) element.className = className;
  if (elementId != null) element.id = elementId;
  if (innerHTML != null) element.innerHTML = innerHTML;

  return element;
}
